/**
 * ASLF Formulas — redesigned homepage interactions (idempotent, editor-aware).
 *
 * Loaded as a module so science scrollytelling can subscribe to Horizon's real
 * scroll container. On desktop ≥990px the document does not scroll —
 * `.page-wrapper` does — so a `window` scroll listener never fires and the
 * pinned chapters stay frozen. See assets/scroll-container.js.
 */
import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const scienceDesktopMq = window.matchMedia('(min-width: 901px)');

/**
 * Unpublished theme 145280237619 synced hero CSS from main but kept a stale
 * `aslf-hero.liquid` (eyebrow → H1 → CTAs). Markup lives in
 * `snippets/aslf-hero-overlay.liquid` (CTAs only, no H1, no science-backed
 * caption). This removes a leftover `.aslf-h-display` and `.aslf-eyebrow`
 * and appends CTAs when Shopify is still serving that old section file.
 * Do not no-op when the heading is already gone.
 *
 * @param {ParentNode} root
 */
function orderHeroOverlay(root) {
  const contents = root.querySelectorAll('.aslf-hero-content');

  for (const content of contents) {
    const ctas = content.querySelector(':scope > .aslf-hero-ctas');
    const heading = content.querySelector(':scope > .aslf-h-display');
    const lede = content.querySelector(':scope > .aslf-lede');
    const leftoverEyebrows = content.querySelectorAll('.aslf-eyebrow');

    if (heading) heading.remove();

    for (const leftover of leftoverEyebrows) {
      leftover.remove();
    }

    if (!ctas) continue;

    content.appendChild(ctas);
    if (lede) content.appendChild(lede);
    content.setAttribute('data-aslf-hero-stack', 'ctas');
  }
}

orderHeroOverlay(document);

/**
 * Keep `.aslf-marquee` filled. Three short phrases duplicated twice are
 * narrower than a desktop viewport, so `translateX(-50%)` scrolled into
 * an empty track. Clone the seed items in each group until that group
 * is at least as wide as the bar and the viewport (390 and 1920);
 * two equal groups + `-50%` then loop.
 *
 * @param {ParentNode} root
 */
function fillMarqueeTrack(root) {
  const marquees = root.querySelectorAll('.aslf-marquee');

  for (const marquee of marquees) {
    const groups = marquee.querySelectorAll(':scope > .aslf-marquee-track > .aslf-marquee-group');

    if (groups.length < 2) continue;

    /* Floor at the viewport so a 1920px bar cannot scroll into a gap
       before layout reports clientWidth. Same phrases, cloned. */
    const minWidth = Math.max(marquee.clientWidth, window.innerWidth || 0, 1);

    for (const group of groups) {
      const seedAttr = group.getAttribute('data-aslf-marquee-seed');
      const seedCount = seedAttr ? Number(seedAttr) : group.children.length;

      if (!seedCount) continue;

      group.setAttribute('data-aslf-marquee-seed', String(seedCount));

      const seeds = Array.prototype.slice.call(group.children, 0, seedCount);
      let guard = 0;

      while (group.scrollWidth < minWidth && guard < 24) {
        for (const seed of seeds) {
          group.appendChild(seed.cloneNode(true));
        }

        guard += 1;
      }
    }
  }
}

/**
 * Do not pause `.aslf-marquee-track` on touch. A play-state of paused
 * leaves the overflow bar clipped. Resume if anything else paused it.
 *
 * @param {ParentNode} root
 */
function keepMarqueeRunning(root) {
  const marquees = root.querySelectorAll('.aslf-marquee');

  for (const marquee of marquees) {
    if (marquee.getAttribute('data-aslf-marquee-running') === '1') continue;

    marquee.setAttribute('data-aslf-marquee-running', '1');

    const resume = () => {
      const track = marquee.querySelector(':scope > .aslf-marquee-track');

      if (!track) return;

      track.style.setProperty('animation-play-state', 'running');

      if (typeof track.getAnimations !== 'function') return;

      for (const animation of track.getAnimations()) {
        if (animation.playState === 'paused') animation.play();
      }
    };

    resume();
    marquee.addEventListener('touchstart', resume, { passive: true });
    marquee.addEventListener('pointerdown', resume, { passive: true });
  }
}

/**
 * @param {ParentNode} root
 */
function initReveals(root) {
  const els = Array.prototype.slice.call(root.querySelectorAll('.aslf-rv:not([data-aslf-rv])'));

  if (!els.length) return;

  els.forEach((el) => {
    el.setAttribute('data-aslf-rv', '1');
  });

  if (reduced || !('IntersectionObserver' in window)) {
    els.forEach((el) => {
      el.classList.add('in');
    });
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  els.forEach((el) => {
    io.observe(el);
  });
}

/**
 * Pinned science chapters: map scroll progress through `.aslf-science-pin-wrap`
 * to which step + visual is active. Must listen on `.page-wrapper` (fallback
 * `window`), not window alone — desktop squeeze-layout never fires window scroll.
 *
 * @param {ParentNode} root
 */
function initScience(root) {
  Array.prototype.slice.call(root.querySelectorAll('.aslf-science:not([data-aslf-init])')).forEach((sci) => {
    sci.setAttribute('data-aslf-init', '1');

    const wrap = sci.querySelector('.aslf-science-pin-wrap');
    const steps = Array.prototype.slice.call(sci.querySelectorAll('.aslf-sci-step'));
    const visuals = Array.prototype.slice.call(sci.querySelectorAll('.aslf-science-visual img'));
    const progress = sci.querySelector('.aslf-science-progress');
    const progressMarks = Array.prototype.slice.call(sci.querySelectorAll('.aslf-science-progress__mark'));

    if (!wrap || !steps.length) return;

    /** @type {EventTarget[]} */
    let scrollTargets = [];
    let ticking = false;
    let visualTimer = 0;
    let lastIndex = -1;

    /**
     * Drive the left-edge chapter rail from the same pin progress as setStep.
     * Not a second scroller — CSS variable + current mark only.
     *
     * @param {number} p
     * @param {number} i
     */
    const setProgress = (p, i) => {
      if (!progress) return;

      progress.style.setProperty('--aslf-science-progress', String(p));
      progress.setAttribute('aria-valuenow', String(i + 1));
      progress.setAttribute('aria-valuetext', `Chapter ${i + 1} of ${steps.length}`);
      progressMarks.forEach((mark, idx) => {
        mark.classList.toggle('is-current', idx === i);
      });
    };

    /**
     * @param {number} i
     */
    const setStep = (i) => {
      if (i === lastIndex) return;
      lastIndex = i;
      steps.forEach((s, idx) => {
        s.classList.toggle('active', idx === i);
      });
      visuals.forEach((v, idx) => {
        v.classList.toggle('active', idx === i);
      });
    };

    /**
     * @param {number} i
     */
    const setVisual = (i) => {
      visuals.forEach((v, idx) => {
        v.classList.toggle('active', idx === i);
      });
    };

    const updateFromScroll = () => {
      ticking = false;
      const r = wrap.getBoundingClientRect();
      const viewportH = getScrollContainer().clientHeight || window.innerHeight;
      const total = r.height - viewportH;

      if (total <= 0) return;

      const p = Math.min(1, Math.max(0, -r.top / total));
      const i = Math.min(steps.length - 1, Math.floor(p * steps.length));
      setStep(i);
      setProgress(p, i);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateFromScroll);
    };

    const stopVisualTimer = () => {
      if (!visualTimer) return;
      clearInterval(visualTimer);
      visualTimer = 0;
    };

    const unbindScroll = () => {
      for (const target of scrollTargets) {
        target.removeEventListener('scroll', onScroll);
      }
      scrollTargets = [];
    };

    const bind = () => {
      unbindScroll();
      stopVisualTimer();
      lastIndex = -1;

      if (!scienceDesktopMq.matches || reduced) {
        steps.forEach((s) => {
          s.classList.add('active');
        });
        setVisual(0);

        if (!reduced && visuals.length > 1) {
          let vi = 0;
          visualTimer = setInterval(() => {
            vi = (vi + 1) % visuals.length;
            setVisual(vi);
          }, 3500);
        }

        return;
      }

      setStep(0);
      setProgress(0, 0);
      /* Horizon desktop: html/body overflow:hidden; real scroller is
         div.page-wrapper. window scroll never fires. Also listen on window
         so 901px–989px (pin on, document still scrolls) still updates. */
      const pageWrapper = document.querySelector('.page-wrapper');
      scrollTargets = pageWrapper ? [pageWrapper, window] : [window];
      for (const target of scrollTargets) {
        target.addEventListener('scroll', onScroll, { passive: true });
      }
      updateFromScroll();
    };

    bind();
    scienceDesktopMq.addEventListener('change', bind);
    scrollContainerMediaQuery.addEventListener('change', bind);
  });
}

/**
 * @param {ParentNode} root
 */
function initStats(root) {
  const nums = Array.prototype.slice.call(root.querySelectorAll('.aslf-stat-num:not([data-aslf-init])'));

  if (!nums.length) return;

  nums.forEach((el) => {
    el.setAttribute('data-aslf-init', '1');
  });

  /**
   * @param {Element} el
   * @param {number} val
   */
  function fmt(el, val) {
    const pre = el.getAttribute('data-prefix') || '';
    const suf = el.getAttribute('data-suffix') || '';
    let out;

    if (el.getAttribute('data-format') === 'int') out = Math.round(val).toLocaleString('en-US');
    else if (Number(el.getAttribute('data-count')) % 1 !== 0) out = val.toFixed(1);
    else out = Math.round(val).toString();

    el.textContent = pre + out + suf;
  }

  /**
   * @param {Element} el
   */
  function runCount(el) {
    const target = parseFloat(el.getAttribute('data-count'));

    if (isNaN(target)) return;

    if (reduced) {
      fmt(el, target);
      return;
    }

    const dur = 1400;
    const t0 = performance.now();

    /**
     * @param {number} now
     */
    function tick(now) {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      fmt(el, target * eased);
      if (p < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  if ('IntersectionObserver' in window && !reduced) {
    const cio = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            runCount(e.target);
            cio.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    nums.forEach((el) => {
      cio.observe(el);
    });
  } else {
    nums.forEach((el) => {
      fmt(el, parseFloat(el.getAttribute('data-count')));
    });
  }
}

/**
 * Remove the pre-footer closer film (Age slowly / Live fully) if leftover
 * JSON or a stale section file still emits it. Do not leave a playing
 * <video> in the DOM.
 *
 * @param {ParentNode} root
 */
function removeCloserBanner(root) {
  const closers = root.querySelectorAll('.aslf-closer');

  for (const closer of closers) {
    for (const video of closer.querySelectorAll('video')) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.removeAttribute('src');
      video.removeAttribute('poster');

      for (const source of video.querySelectorAll('source')) {
        source.removeAttribute('src');
        source.remove();
      }

      try {
        video.pause();
        video.load();
      } catch {
        /* Detached or unimplemented media. */
      }

      video.remove();
    }

    const wrap = closer.closest('.shopify-section');

    (wrap || closer).remove();
  }
}

/**
 * Remove leftover homepage #aslf-all-reviews. Browse lives on
 * /pages/reviews. Do not leave a homepage-bottom reviews substitute.
 *
 * @param {ParentNode} root
 */
function removeHomeAllReviews(root) {
  const leftovers = root.querySelectorAll('#aslf-all-reviews, .aslf-all-reviews');

  for (const leftover of leftovers) {
    const wrap = leftover.closest('.shopify-section');

    (wrap || leftover).remove();
  }
}

/**
 * @param {ParentNode} root
 */
function initFaq(root) {
  Array.prototype.slice.call(root.querySelectorAll('.aslf-faq-item:not([data-aslf-init])')).forEach((item) => {
    item.setAttribute('data-aslf-init', '1');
    const q = item.querySelector('.aslf-faq-q');

    if (!q) return;

    q.setAttribute('aria-expanded', 'false');
    q.addEventListener('click', () => {
      const open = item.classList.toggle('open');
      q.setAttribute('aria-expanded', String(open));
    });
  });
}

/**
 * Clear leftover inline hero padding from older deploys.
 * Desktop overlays the transparent nav on the still (no gray pad).
 * Mobile stays in-flow below the header stack (no pad).
 *
 * @param {ParentNode} [root]
 */
function padHeroBelowHeader(root) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const heroes = scope.querySelectorAll('.aslf-hero');

  for (const hero of heroes) {
    hero.style.removeProperty('padding-top');
  }
}

/**
 * @param {ParentNode} [root]
 */
function initAll(root) {
  root = root || document;
  orderHeroOverlay(root);
  padHeroBelowHeader(root);
  fillMarqueeTrack(root);
  keepMarqueeRunning(root);
  requestAnimationFrame(() => {
    fillMarqueeTrack(root);
    keepMarqueeRunning(root);
    padHeroBelowHeader(root);
  });
  initReveals(root);
  initScience(root);
  initStats(root);
  removeCloserBanner(root);
  removeHomeAllReviews(root);
  initFaq(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAll(document);
  });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (e) => {
  initAll(e.target);
});

window.addEventListener('resize', () => {
  fillMarqueeTrack(document);
  padHeroBelowHeader(document);
});
